import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProtectedRoute = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // First check if we have stored user data (optimistic)
      const storedUser = localStorage.getItem('user');
      const token = localStorage.getItem('token');
      
      // If we have stored user, show content immediately while verifying
      if (storedUser) {
        setIsAuthenticated(true);
      }

      try {
        // Verify with backend - supports both JWT (token) and session cookie (Google OAuth)
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true, // Send session cookie for Google OAuth
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.status === 200 && response.data) {
          setIsAuthenticated(true);
          // Update stored user data with fresh data from server
          localStorage.setItem('user', JSON.stringify(response.data));
        } else {
          // Only clear if we don't have stored data
          if (!storedUser) {
            setIsAuthenticated(false);
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error.response?.status);
        // Only redirect to login if:
        // 1. Backend explicitly returns 401 (unauthorized)
        // 2. AND we don't have any stored user data
        if (error.response?.status === 401 && !storedUser) {
          setIsAuthenticated(false);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
        // For network errors or other issues, keep user logged in if they have stored data
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [location.pathname]); // Only re-check on route change

  useEffect(() => {
    if (!isLoading && isAuthenticated === false) {
      navigate('/login', { replace: true });
    }
  }, [isLoading, isAuthenticated, navigate]);

  if (isLoading && isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !isLoading) {
    return null;
  }

  return children;
};
